import React, { Component, Fragment } from 'react';
import { withStyles } from "@material-ui/core/styles";
import './ManageRosters.css';
import AuthenticationService from "../../../Services/AuthenticationService";
import { NavLink } from 'react-router-dom';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import GlobalMessage from "../../GlobalMessage";
import LoadingMsgBar from "../../LoadingMsgBar";
import { isEmpty } from "../../../utils/Utils";
import { isNotEmpty } from "../../../utils/Validator";
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import UploadRoster from './UploadRoster';

const styles = theme => ({
    root: {
        width: '100%',
    },
    button: {
        margin: theme.spacing(1),
        cursor: 'pointer',
        color: '#0f197d'
    },
    input: {
        display: 'none',
    },
});

window.analytics = { pageName: 'Manage Rosters: List View' };

// Helper function for sorting
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a, b, orderBy) {
    const valueA = a[orderBy] !== null ? String(a[orderBy]).toUpperCase() : '';
    const valueB = b[orderBy] !== null ? String(b[orderBy]).toUpperCase() : '';
    
    if (valueB < valueA) {
        return -1;
    }
    if (valueB > valueA) {
        return 1;
    }
    return 0;
}

class ManageRosters extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rosters: [],
            filteredRosters: [], 
            selected: [],
            username: '',
            grpId: '',
            sourceIds: [],
            sourceGroups: [],
            isMirror: true,
            errorMsg: '',
            successMessage: '',
            page: 0,
            rowsPerPage: 10,
            order: 'desc',  // Change from 'asc' to 'desc'
            orderBy: 'uploadDate',
            showUploadRoster: false,
            isLoading: false
        };
    }

    componentDidMount() {
        this.getUser();
    }

    getUser = () => {
        let currentUser = AuthenticationService.getMirrorUser();
        if(isEmpty(currentUser)) {
            currentUser = AuthenticationService.getLoggedInUserName();
            this.setState({isMirror: false});
        }
        
        this.setState({ username: currentUser });
        
        AuthenticationService.getUsrDtls(currentUser)
            .then((response) => {
                this.setState({grpId: response.data.grpInfoId});
                this.loadRosterData();
            }).catch((error) => {
                console.log('Error retrieving user details:', error);
                this.setState({
                    errorMsg: 'Error: There was a problem retrieving user information. Please try again later.'
                });
            });
    }

    // Load roster data based on user's group ID
    loadRosterData = () => {
        // First, fetch source IDs and roster source groups for the user's group
        this.loadSourceIds();
        this.loadRosterSourceGroups();
        
        // Then, fetch roster data from the API
        if (!this.state.grpId) {
            console.log('No group ID available to fetch roster data');
            return;
        }
        
        // Set loading state
        this.setState({ isLoading: true });
        
        // Call the API endpoint to get upload logs for this group
        AuthenticationService.getRosterUploadLogs(this.state.grpId)
            .then((response) => {
                // Process the response data
                const rosters = response.data.map(log => {
                    // Format date from the response
                    const uploadDate = new Date(log.uploadDt);
                    const formattedDate = uploadDate.toLocaleDateString('en-US', { 
                        month: '2-digit', 
                        day: '2-digit', 
                        year: 'numeric' 
                    });
                    
                    // Map the response data to match our table structure
                    return {
                        id: log.logId,
                        providerGroup: log.grpName || 'N/A',
                        uploadDate: formattedDate,
                        updateType: log.updateType || 'N/A',
                        uploadedBy: log.uploadedByUserName || 'N/A', // This might need adjustment based on actual response
                        fileNames: log.fileName || 'N/A',
                        sourceId: log.sourceId
                    };
                });
                
                console.log('Roster data retrieved:', rosters);
                this.setState({ 
                    rosters, 
                    filteredRosters: rosters,
                    isLoading: false 
                });
            })
            .catch(error => {
                console.log('Error fetching roster data:', error);
                this.setState({ 
                    errorMsg: 'Error: Unable to retrieve roster upload history. Please try again later.',
                    isLoading: false
                });
            });
    }
    
    // Load source IDs for the user's group
    loadSourceIds = () => {
        if (!this.state.grpId) {
            console.log('No group ID available to fetch source IDs');
            return;
        }
        
        AuthenticationService.getSourceIdsForUser(this.state.grpId)
            .then((response) => {
                console.log('Source IDs retrieved:', response.data);
                this.setState({ sourceIds: response.data });
            })
            .catch(error => {
                console.log('Error fetching source IDs:', error);
                this.setErrorMsg('Error: Unable to retrieve provider source information. Please try again later.');
            });
    }
    
    // Load roster source groups for the user's group
    loadRosterSourceGroups = () => {
        if (!this.state.grpId) {
            console.log('No group ID available to fetch roster source groups');
            return;
        }
        
        AuthenticationService.getRosterSourceGroupsForUser(this.state.grpId)
            .then((response) => {
                console.log('Roster source groups retrieved:', response.data);
                const sortedGroups = response.data.sort((a, b) => {
                    const nameA = a.grpName || '';
                    const nameB = b.grpName || '';
                    return nameA.localeCompare(nameB);
                });
                this.setState({ sourceGroups: sortedGroups });
            })
            .catch(error => {
                console.log('Error fetching roster source groups:', error);
                this.setErrorMsg('Error: Unable to retrieve provider group information. Please try again later.');
            });
    }
    
    setErrorMsg = (message = 'Error: There was an error retrieving data. Please try again or contact Customer Service.') => {
        this.setState({ errorMsg: message });
    }
    
    // Additional methods will be added here for roster management

    handleUpload = () => {
        this.setState({ 
            showUploadRoster: true,
            successMessage: '', // Clear any previous success messages
            errorMsg: ''        // Clear any previous error messages
        });
    }

    handleUploadCancel = () => {
        this.setState({ showUploadRoster: false });
    }

    handleUploadComplete = () => {
        this.setState({ 
            showUploadRoster: false,
            successMessage: 'Roster file has been uploaded successfully.'
        });
        
        // Clear any previous error message
        this.setState({ errorMsg: '' });
        
        // Refresh the roster data after a successful upload
        this.loadRosterData();
    }

    handleDownload = () => {
        // TODO: Implement download functionality
        console.log('Download button clicked');
    }
    
    handleChangePage = (event, newPage) => {
        this.setState({ page: newPage });
    };
    
    handleChangeRowsPerPage = (event) => {
        this.setState({ 
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0 
        });
    }
    
    handleRequestSort = (event, property) => {
        const isAsc = this.state.orderBy === property && this.state.order === 'asc';
        this.setState({
            order: isAsc ? 'desc' : 'asc',
            orderBy: property
        });
    };
    
    handleFilter = (value, property) => {
        const filtered = this.state.rosters.filter(item => {
            if (!value) return true;
            const itemValue = item[property]?.toString().toLowerCase() || '';
            return itemValue.includes(value.toLowerCase());
        });
        
        this.setState({
            filteredRosters: filtered,
            page: 0
        });
    }

    render() {
        if (this.state.showUploadRoster) {
            return (
                <UploadRoster 
                    isMirror={this.state.isMirror}
                    onCancel={this.handleUploadCancel}
                    onUploadComplete={this.handleUploadComplete}
                    sourceIds={this.state.sourceIds}
                    sourceGroups={this.state.sourceGroups}
                    grpId={this.state.grpId}
                    username={this.state.username}
                />
            );
        }
        
        return (
            <Fragment>
                <div className='Manage-rosters App-content'>
                    
                    <h1 className='App-page-label' style={{ display: 'inline-block', margin: 0 }}>Manage Rosters</h1>
                    <GlobalMessage message={this.state.errorMsg} />
                    <GlobalMessage type='success' message={this.state.successMessage} />
                    
                    <div style={{ marginTop: '10px' }}>
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={this.handleUpload}
                            style={{ marginRight: '10px' }}
                            aria-label='Manage Rosters: UPLOAD Button'
                        >
                            UPLOAD...
                        </Button>
                        {/* <Button 
                            variant="contained"
                            color="primary"
                            onClick={this.handleDownload}
                            aria-label='Manage Rosters: DOWNLOAD Button'
                        >
                            DOWNLOAD...
                        </Button> */}
                    </div>

                    
                    <div style={{ marginTop: '20px' }}>
                        <h2 className='App-page-label' style={{ fontSize: '16px' }}>Upload History</h2>
                    </div>
                    
                    {this.state.isLoading && <LoadingMsgBar label="Loading roster data..." />}
                    
                    <div className='Manage-rosters-content'>
                        <Paper className="Manage-rosters-table">
                            <div className="top-pagination">
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={(this.state.filteredRosters.length > 0 ? this.state.filteredRosters.length : this.state.rosters.length)}
                                    rowsPerPage={this.state.rowsPerPage}
                                    page={this.state.page}
                                    onPageChange={this.handleChangePage}
                                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                                    labelRowsPerPage="Rows per page:"
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                                />
                            </div>
                            <div className="Manage-rosters-table-wrapper">
                                <Table className="Manage-rosters-table" aria-label="Upload history table">
                                    <TableHead className="Manage-rosters-head">
                                        <TableRow className="header-row">
                                            <TableCell>
                                                <TableSortLabel
                                                    active={this.state.orderBy === 'providerGroup'}
                                                    direction={this.state.orderBy === 'providerGroup' ? this.state.order : 'asc'}
                                                    onClick={(e) => this.handleRequestSort(e, 'providerGroup')}
                                                >
                                                    <div className='Manage-rosters-tbl-hdr'>
                                                        Provider Group
                                                    </div>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={this.state.orderBy === 'uploadDate'}
                                                    direction={this.state.orderBy === 'uploadDate' ? this.state.order : 'asc'}
                                                    onClick={(e) => this.handleRequestSort(e, 'uploadDate')}
                                                >
                                                    <div className='Manage-rosters-tbl-hdr'>
                                                        Upload Date
                                                    </div>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={this.state.orderBy === 'updateType'}
                                                    direction={this.state.orderBy === 'updateType' ? this.state.order : 'asc'}
                                                    onClick={(e) => this.handleRequestSort(e, 'updateType')}
                                                >
                                                    <div className='Manage-rosters-tbl-hdr'>
                                                        Update Type
                                                    </div>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={this.state.orderBy === 'uploadedBy'}
                                                    direction={this.state.orderBy === 'uploadedBy' ? this.state.order : 'asc'}
                                                    onClick={(e) => this.handleRequestSort(e, 'uploadedBy')}
                                                >
                                                    <div className='Manage-rosters-tbl-hdr'>
                                                        Uploaded By
                                                    </div>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={this.state.orderBy === 'fileNames'}
                                                    direction={this.state.orderBy === 'fileNames' ? this.state.order : 'asc'}
                                                    onClick={(e) => this.handleRequestSort(e, 'fileNames')}
                                                >
                                                    <div className='Manage-rosters-tbl-hdr'>
                                                        File Names
                                                    </div>
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow className='filter-row'>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                                <TextField
                                                    placeholder="Filter…"
                                                    margin="dense"
                                                    variant="outlined"
                                                    style={{ width: '100%' }}
                                                    onChange={(e) => this.handleFilter(e.target.value, 'providerGroup')}
                                                />
                                            </TableCell>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                                <TextField
                                                    placeholder="Filter…"
                                                    margin="dense"
                                                    variant="outlined"
                                                    style={{ width: '100%' }}
                                                    onChange={(e) => this.handleFilter(e.target.value, 'uploadDate')}
                                                />
                                            </TableCell>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                                <TextField
                                                    placeholder="Filter…"
                                                    margin="dense"
                                                    variant="outlined"
                                                    style={{ width: '100%' }}
                                                    onChange={(e) => this.handleFilter(e.target.value, 'updateType')}
                                                />
                                            </TableCell>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                                <TextField
                                                    placeholder="Filter…"
                                                    margin="dense"
                                                    variant="outlined"
                                                    style={{ width: '100%' }}
                                                    onChange={(e) => this.handleFilter(e.target.value, 'uploadedBy')}
                                                />
                                            </TableCell>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
                                                <TextField
                                                    placeholder="Filter…"
                                                    margin="dense"
                                                    variant="outlined"
                                                    style={{ width: '100%' }}
                                                    onChange={(e) => this.handleFilter(e.target.value, 'fileNames')}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stableSort(
                                            this.state.filteredRosters.length > 0 ? this.state.filteredRosters : this.state.rosters,
                                            getComparator(this.state.order, this.state.orderBy)
                                        )
                                        .slice(this.state.page * this.state.rowsPerPage, this.state.page * this.state.rowsPerPage + this.state.rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell component="th" scope="row">
                                                    {row.providerGroup}
                                                </TableCell>
                                                <TableCell>{row.uploadDate}</TableCell>
                                                <TableCell>
                                                    {row.updateType === 'full' ? 'Full' : 'Partial (A/C/T)'}
                                                </TableCell>
                                                <TableCell>{row.uploadedBy}</TableCell>
                                                <TableCell>{row.fileNames}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={(this.state.filteredRosters.length > 0 ? this.state.filteredRosters.length : this.state.rosters.length)}
                                rowsPerPage={this.state.rowsPerPage}
                                page={this.state.page}
                                onPageChange={this.handleChangePage}
                                onRowsPerPageChange={this.handleChangeRowsPerPage}
                                labelRowsPerPage="Rows per page:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                                className="bottom-pagination"
                            />
                        </Paper>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default withStyles(styles)(ManageRosters);
